import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import ExpressiveXLText from 'shared/components/vxl/typography/expressive/ExpressiveXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

export interface LendingCardsProps extends PressableAnalyticsProps {
  text?: string;
  details?: LendingCardDetailsProps[];
  onPress?: () => void;
  isDisabled?: boolean;
  isSmallCard?: boolean;
}

export interface LendingCardDetailsProps {
  id?: number;
  titleText: string;
  subtitleText: string;
}

const LendingCard = ({
  isDisabled = false,
  text,
  details,
  onPress,
  isSmallCard = false,
  analyticsId = '',
  customAttributes = null,
}: LendingCardsProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const cardAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  return (
    <Pressable
      role="button"
      disabled={isDisabled}
      testID="Amount_tile"
      aria-disabled={isDisabled}
      accessible
      onPress={() => {
        cardAnalytics();
        onPress?.();
      }}
      onBlur={() => setIsFocused(false)}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      style={[
        styles.container,
        isFocused && styles.containerFocused,
        isHovered && styles.containerHovered,
        isPressed && styles.containerPressed,
        isSmallCard && styles.smallWidth,
      ]}
    >
      <ExpressiveXLText
        style={[styles.text, isDisabled && styles.mutedText]}
        ellipsizeMode="tail"
        numberOfLines={1}
      >
        {text}
      </ExpressiveXLText>
      <View style={styles.detailsContainer}>
        {details?.map((item) => (
          <View style={styles.detailsItemWrapper} key={item.id}>
            <BodyXSText
              style={[styles.titleText, isDisabled && styles.mutedText]}
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {item?.titleText}
            </BodyXSText>
            <BodyBaseText
              style={[styles.subtitleText, isDisabled && styles.mutedText]}
              ellipsizeMode="tail"
              numberOfLines={1}
            >
              {item?.subtitleText}
            </BodyBaseText>
          </View>
        ))}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.border.primarySolid,
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
    // ToDo: JT-133128 Update this logic after Font RN
    //  upgrade to use theme.spacing.xl  all the time for Vertical padding
    paddingTop: Platform.OS === 'web' ? theme.spacing.xl : 12,
  },
  containerFocused: {
    outlineColor: theme.color.interactive.focused.border,
  },
  containerHovered: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  containerPressed: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
  detailsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.m,
  },
  detailsItemWrapper: { flex: 1 },
  mutedText: {
    color: theme.color.interactive.disabled.text,
  },
  smallWidth: {
    width: 167.5,
  },
  subtitleText: {
    color: theme.color.text.primary,
  },
  text: {
    color: theme.color.text.primary,
    lineHeight: 60,
  },
  titleText: {
    color: theme.color.text.secondary,
  },
});

export default LendingCard;
